import React, { useState, useEffect } from 'react';
import { AccountTree, Settings } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import { DynamicContainer } from '../components/common/DynamicContainer';
import { toast } from 'react-toastify';
import RelationshipDrawer from './GlobalLinkRelationshipDrawer/RelationshipDrawer';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/global-link-relationships`;

const GlobalLinkRelationship = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [headers, setHeaders] = useState([]);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedHeader, setSelectedHeader] = useState(null);

    // Check if user is CDX user
    const isCdxUser = () => {
        try {
            const cdxUser = localStorage.getItem('cdx_user');
            return cdxUser === 'true';
        } catch (error) {
            console.error('Error reading cdx_user from localStorage:', error);
            return false;
        }
    };

    // Fetch headers on component mount
    useEffect(() => {
        fetchHeaders();
    }, []);

    const fetchHeaders = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            console.log('Fetching link relationship headers...');
            console.log('API URL:', `${API_BASE_URL}/headers`);
            const response = await fetch(`${API_BASE_URL}/headers`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log('Response:', data);

            // Map the response data to match column definitions and add an id field
            const mappedData = data.map(item => ({
                ...item,
                // Use linkHeaderId for CRUD operations
                id: item.linkHeaderId,
            }));

            console.log('Mapped data for grid:', mappedData);
            setHeaders(mappedData);

            return mappedData;
        } catch (error) {
            console.error('Error details:', error);
            toast.error(`Error fetching link relationship headers: ${error.message}`);
            return [];
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreate = async (data) => {
        try {
            // Remove empty fields or null values
            const cleanData = Object.fromEntries(
                Object.entries(data).filter(([_, v]) => v !== null && v !== '')
            );

            // Add current user as createdBy
            cleanData.createdBy = localStorage.getItem('username') || 'admin';

            const token = localStorage.getItem('token');
            console.log('Creating header with clean data:', cleanData);

            const response = await fetch(`${API_BASE_URL}/headers`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(cleanData)
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            const responseData = await response.json();
            console.log('Create response:', responseData);
            toast.success('Link relationship header created successfully');
            await fetchHeaders();
            return responseData;
        } catch (error) {
            console.error('Create error full details:', error);
            toast.error(`Error creating header: ${error.message}`);
            throw error;
        }
    };

    const handleUpdate = async (id, data) => {
        try {
            const token = localStorage.getItem('token');
            console.log('Updating header:', id, data);

            const response = await fetch(`${API_BASE_URL}/headers/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(data)
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            const responseData = await response.json();
            console.log('Update response:', responseData);
            toast.success('Link relationship header updated successfully');
            await fetchHeaders();
            return responseData;
        } catch (error) {
            console.error('Update error full details:', error);
            toast.error(`Error updating header: ${error.message}`);
            throw error;
        }
    };

    const handleDelete = async (id) => {
        try {
            const token = localStorage.getItem('token');
            console.log('Deleting header:', id);

            const response = await fetch(`${API_BASE_URL}/headers/${id}`, {
                method: 'DELETE',
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            toast.success('Link relationship header deleted successfully');
            await fetchHeaders();
            return true;
        } catch (error) {
            console.error('Delete error full details:', error);
            toast.error(`Error deleting header: ${error.message}`);
            throw error;
        }
    };

    // Function to open the relationship drawer
    const handleOpenRelationshipDrawer = (header) => {
        setSelectedHeader(header);
        setDrawerOpen(true);
    };

    // Columns matching MasterConnector pattern
    const columns = [
        { field: 'linkHeaderId', headerName: 'ID', width: 70 },
        { field: 'linkName', headerName: 'Link Name', width: 200 },
        { field: 'domain', headerName: 'Domain', width: 150 },
        { field: 'description', headerName: 'Description', width: 250 },
        { field: 'primaryTableName', headerName: 'Primary Table', width: 180 },
        { field: 'usageScope', headerName: 'Usage Scope', width: 120 },
        { field: 'createdBy', headerName: 'Created By', width: 150 },
        {
            field: 'actions',
            headerName: 'Relationships',
            width: 120,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Tooltip title="Manage Relationships">
                    <IconButton
                        onClick={(e) => {
                            e.stopPropagation(); // Prevent row selection
                            handleOpenRelationshipDrawer(params.row);
                        }}
                        size="small"
                    >
                        <Settings fontSize="small" />
                    </IconButton>
                </Tooltip>
            )
        }
    ];

    // Form fields matching MasterConnector pattern
    const formFields = [
        {
            name: 'linkName',
            label: 'Link Name',
            required: true,
            validate: (value) => {
                if (!value) return 'Link Name is required';
                if (value.length < 3) return 'Link Name must be at least 3 characters';
                return null;
            }
        },
        {
            name: 'domain',
            label: 'Domain',
            placeholder: 'e.g., ERP, CRM, Project Management'
        },
        {
            name: 'description',
            label: 'Description',
            required: true,
            multiline: true,
            rows: 4,
            validate: (value) => {
                if (!value) return 'Description is required';
                return null;
            }
        },
        {
            name: 'aiContext',
            label: 'AI Context',
            required: true,
            multiline: true,
            rows: 4,
            placeholder: 'Provide context for AI to understand this relationship...',
            validate: (value) => {
                if (!value) return 'AI Context is required';
                return null;
            }
        },
        {
            name: 'primaryTableName',
            label: 'Primary Table Name',
            placeholder: 'Main table in this relationship set'
        },
        {
            name: 'usageScope',
            label: 'Usage Scope',
            type: 'select',
            options: ['RAG', 'QUERY_BUILDER', 'BOTH']
        }
    ];

    const initialFormData = {
        linkName: '',
        domain: null,
        description: '',
        aiContext: '',
        primaryTableName: null,
        usageScope: 'RAG'
    };

    return (
        <>
            <DynamicContainer
                title="Global Link Relationships"
                icon={AccountTree}
                columns={columns}
                formFields={formFields}
                initialFormData={initialFormData}
                idField="id"
                rows={headers}
                onCreate={handleCreate}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
                isLoading={isLoading}
                isCdxUser={isCdxUser()}
                functionCode="Global Link Relationships"
            />

            {/* Relationship Drawer */}
            <RelationshipDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                headerId={selectedHeader?.id}
                headerName={selectedHeader?.linkName}
            />
        </>
    );
};

export default GlobalLinkRelationship;
